import Box from "@mui/material/Box";
import { useRef } from "react";

import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import { MID, NOT_WIDE, TINY } from "@/components/marketing/tokens";
import {
  TRUSTPILOT_BUSINESS_UNIT,
  TRUSTPILOT_MINI,
  TRUSTPILOT_REVIEWS,
  useTrustpilotWidget,
} from "./trustpilot";

/** The one tint every hover on this row takes, at the strength each sets. */
const HOVER_GREEN = "rgba(26, 179, 148, 0.11)";
const HOVER_RED = "rgba(247, 2, 65, 0.08)";

/**
 * `.social_proof_list` on the campaign heroes: the small Trustpilot lockup,
 * then the partner review site's logo linking to its review of Webshare. The
 * widget draws 100 wide inside a box 115 wide padded 72 on the left, and
 * spills to the right as published.
 */
export default function HeroReviewRow({
  review,
  fill,
}: {
  review: { href: string; logo: string; newTab?: boolean };
  /**
   * `.proxyway_wrap` without `is-ppi`: on a phone the badge keeps the row's
   * width and shows its tint at rest, where `is-ppi` closes to its logo.
   */
  fill?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  useTrustpilotWidget(host);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "4.8px",
        width: 1,
        [NOT_WIDE]: { justifyContent: "center" },
        [TINY]: { gap: "16px" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "115.2px",
          height: "80px",
          p: "8px 8px 8px 72px",
          borderRadius: "8px",
          overflow: "visible",
          "&:hover": { backgroundColor: HOVER_GREEN },
        }}
      >
        {/* `.trustpilot-widget`: the site sets every widget at least 160 wide,
            which is what centres this 100-wide lockup 30 left of its box. */}
        <Box
          ref={host}
          className="trustpilot-widget"
          sx={{ flex: "none", minWidth: "160px" }}
          data-locale="en-GB"
          data-template-id={TRUSTPILOT_MINI}
          data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT}
          data-style-height="42"
          data-style-width="100"
          data-theme="light"
        >
          {/* The published widget's own fallback, which the bootstrap replaces with its iframe. */}
          <a href={TRUSTPILOT_REVIEWS} target="_blank" rel="noopener">
            Trustpilot
          </a>
        </Box>
      </Box>
      <Box
        component="a"
        href={marketingHref(review.href)}
        {...newTabProps(review)}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "8px 11.2px",
          borderRadius: "8px",
          "&:hover": { backgroundColor: HOVER_RED },
          [MID]: { width: 1 },
          [TINY]: fill
            ? { height: "40px", py: "4.8px", backgroundColor: HOVER_RED }
            : { width: "auto", height: "40px", py: "4.8px" },
        }}
      >
        <Box
          component="img"
          src={review.logo}
          alt=""
          loading="eager"
          sx={{ width: "84.96px" }}
        />
      </Box>
    </Box>
  );
}
